import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { KeyboardEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { recomputeAllScores } from '../../api/services/qualityControlService'
import { useCurrentSite } from '../../store/store.hooks'

interface AdminToolsPanelProps {
  onRecomputeSuccess?: (count: number) => void
  onRecomputeError?: () => void
}

const AdminToolsPanel = ({ onRecomputeSuccess, onRecomputeError }: AdminToolsPanelProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentSite = useCurrentSite()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { mutate: recompute, isPending: isRecomputing } = useMutation({
    mutationFn: () => recomputeAllScores(currentSite?.id ?? ''),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quality-tests'] })
      setConfirmOpen(false)
      setPopoverOpen(false)
      onRecomputeSuccess?.(data.recomputedCount)
    },
    onError: () => {
      setConfirmOpen(false)
      setPopoverOpen(false)
      onRecomputeError?.()
    },
  })

  const handleMenuKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') setPopoverOpen(false)
  }

  const handleCreateNew = () => {
    setPopoverOpen(false)
    navigate('/quality-control/create')
  }

  const handleRecomputeRequest = () => {
    setConfirmOpen(true)
  }

  return (
    <>
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}>
        <Button
          ref={anchorRef}
          variant="contained"
          color="secondary"
          startIcon={<SettingsIcon />}
          aria-label={t('qualityControl.adminTools')}
          aria-expanded={popoverOpen}
          aria-haspopup="true"
          onClick={() => setPopoverOpen((prev) => !prev)}
          sx={{ borderRadius: 999 }}
        >
          {t('qualityControl.adminTools')}
        </Button>
      </Box>

      <Popover
        open={popoverOpen}
        disableScrollLock
        anchorEl={anchorRef.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <Box role="menu" onKeyDown={handleMenuKeyDown}>
          <ListItemButton
            role="menuitem"
            onClick={handleCreateNew}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateNew()
              if (e.key === 'Escape') setPopoverOpen(false)
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('qualityControl.createTest')} />
          </ListItemButton>

          <Divider />

          <ListItemButton
            role="menuitem"
            onClick={handleRecomputeRequest}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRecomputeRequest()
              if (e.key === 'Escape') setPopoverOpen(false)
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('qualityControl.recomputeAllScores')} />
          </ListItemButton>
        </Box>
      </Popover>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('qualityControl.recomputeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('qualityControl.recomputeConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isRecomputing}>
            {t('qualityControl.cancel')}
          </Button>
          <Button variant="contained" onClick={() => recompute()} disabled={isRecomputing}>
            {isRecomputing ? t('qualityControl.recomputingScores') : t('qualityControl.recomputeAllScores')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AdminToolsPanel
